import React, { useState } from 'react';
import { Event } from '../types';
import { format, parseISO } from 'date-fns';
import { calendar } from '../utils/calendar';

interface EventCardProps {
  event: Event;
  featured?: boolean;
}

const EventCard: React.FC<EventCardProps> = ({ event, featured = false }) => {
  const [showDropdown, setShowDropdown] = useState(false);

  const eventDate = parseISO(event.date);
  const formattedDate = format(eventDate, 'MMM dd, yyyy');
  const formattedTime = event.start_time;

  const handleAddToCalendar = (method: 'google' | 'whatsapp' | 'ical') => {
    const calendarEvent = {
      title: event.title,
      description: event.description,
      location: event.location,
      startTime: `${event.date}T${event.start_time}`,
      endTime: `${event.date}T${event.end_time}`,
    };

    if (method === 'google') {
      window.open(calendar.generateGoogleCalendarLink(calendarEvent), '_blank');
    } else if (method === 'whatsapp') {
      const message = calendar.generateWhatsAppMessage(calendarEvent);
      window.open(`https://wa.me/?text=${message}`, '_blank');
    } else if (method === 'ical') {
      calendar.downloadICalFile(calendarEvent, `${event.id}.ics`);
    }

    setShowDropdown(false);
  };

  return (
    <div
      className={`bg-white rounded-lg shadow-md hover:shadow-lg transition ${
        featured ? 'border-2 border-[#C41230]' : ''
      }`}
    >
      {featured && (
        <div className="bg-[#C41230] text-white text-center py-2 font-semibold text-sm">
          ⭐ Featured Event
        </div>
      )}

      <div className="p-6">
        {/* Event Type Badge */}
        <div className="flex flex-wrap gap-2 mb-3">
          {event.tags?.slice(0, 3).map((tag, index) => (
            <span
              key={index}
              className="px-2 py-1 bg-gray-100 text-gray-700 text-xs rounded-full"
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Title */}
        <h3 className="text-xl font-bold text-gray-900 mb-2 line-clamp-2">{event.title}</h3>

        {/* Date and Time */}
        <div className="flex items-center text-gray-600 mb-2">
          <span className="mr-2">📅</span>
          <span>{formattedDate}</span>
        </div>

        <div className="flex items-center text-gray-600 mb-2">
          <span className="mr-2">🕐</span>
          <span>
            {formattedTime} ({event.duration})
          </span>
        </div>

        {/* Venue */}
        <div className="flex items-center text-gray-600 mb-4">
          <span className="mr-2">📍</span>
          <span className="text-sm">{event.venue}</span>
        </div>

        {/* Speaker/Facilitator */}
        {(event.speaker || event.instructor || event.facilitator) && (
          <div className="flex items-center text-gray-700 mb-4 text-sm">
            <span className="mr-2">👤</span>
            <span className="font-semibold">
              {event.speaker || event.instructor || event.facilitator}
            </span>
          </div>
        )}

        {/* Description */}
        <p className="text-gray-600 text-sm mb-4 line-clamp-3">{event.description}</p>

        {/* Actions */}
        <div className="flex gap-2 relative">
          {event.rsvp_link && (
            <a
              href={event.rsvp_link}
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-[#C41230] text-white py-2 px-4 rounded-lg hover:bg-[#A01028] transition text-center text-sm font-semibold"
            >
              RSVP
            </a>
          )}

          <div className="relative">
            <button
              onClick={() => setShowDropdown(!showDropdown)}
              className="bg-gray-200 text-gray-700 py-2 px-4 rounded-lg hover:bg-gray-300 transition text-sm font-semibold whitespace-nowrap"
            >
              📅 Add to Calendar
            </button>

            {showDropdown && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-50">
                <button
                  onClick={() => handleAddToCalendar('google')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center"
                >
                  <span className="mr-2">📆</span>
                  Google Calendar
                </button>
                <button
                  onClick={() => handleAddToCalendar('whatsapp')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center"
                >
                  <span className="mr-2">💬</span>
                  WhatsApp Reminder
                </button>
                <button
                  onClick={() => handleAddToCalendar('ical')}
                  className="w-full text-left px-4 py-2 hover:bg-gray-100 flex items-center"
                >
                  <span className="mr-2">📥</span>
                  Download iCal
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Capacity Info */}
        {event.capacity && event.registration_required && (
          <div className="mt-3 text-xs text-gray-500 flex items-center">
            <span className="mr-1">ℹ️</span>
            <span>Limited to {event.capacity} participants • Registration required</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default EventCard;
